import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SERVICES } from "../config";

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const c = {
    bg: "#060a14",
    card: "#111827",
    text: "#e2e8f0",
    sub: "#94a3b8",
    muted: "#64748b",
    accent: "#4fc3f7",
    green: "#10b981",
    border: "#1e293b",
  };

  const pricingExamples = [
    { box: "10 lb Box (Ground)", competitor: "Pirate Ship", competitorPrice: "$14.80", ourPrice: "$9.50", save: "$5.30" },
    { box: "20 lb Box (Ground)", competitor: "Pirate Ship", competitorPrice: "$22.50", ourPrice: "$14.00", save: "$8.50" },
    { box: "30 lb Box (Ground)", competitor: "Pirate Ship", competitorPrice: "$58.00", ourPrice: "$37.00", save: "$21.00" },
    { box: "50 lb Box (Ground)", competitor: "Pirate Ship", competitorPrice: "$72.00", ourPrice: "$48.00", save: "$24.00" },
  ];

  const faqItems = [
    { q: "Are these labels 100% real?", a: "Yes — every label we provide is a 100% legitimate UPS shipping label. They come with full tracking, real delivery confirmation, and are fully insured through UPS. These are not gray market or resold leftovers. You get the same exact label UPS would give you at the counter, just at a fraction of the price." },
    { q: "Do labels come with insurance?", a: "Absolutely. All labels include standard UPS liability coverage. You can also add additional declared value insurance when placing your order for high-value items. Your packages are protected just like any other UPS shipment." },
    { q: "How do I connect my account to VEEQO?", a: "Message us through the in-app chat once you've created your account and we'll walk you through linking your ShipFast account to VEEQO. It's a quick setup that lets you manage all your shipments from one place." },
    { q: "How fast do I get my label?", a: "Most labels are generated within 1 minute of confirming your order. As soon as your label is ready, you'll get a notification and can download it instantly from your dashboard." },
    { q: "How do I pay?", a: "We use a simple balance system. Fund your account via Zelle, and your balance is automatically deducted when you confirm an order. No credit card on file, no monthly fees, no contracts." },
    { q: "Is there a minimum order or monthly fee?", a: "Nope. No minimums, no monthly fees, no contracts, no hidden charges. You only pay for the labels you use. Ship one package or a thousand — it's up to you." },
    { q: "What UPS services do you offer?", a: "We offer UPS Ground, UPS 3 Day Select, UPS 2nd Day Air, and UPS Next Day Air. All domestic services with full tracking and delivery confirmation." },
  ];

  const sectionStyle = (extra) => ({
    padding: "80px 24px",
    background: c.bg,
    position: "relative",
    ...extra,
  });

  const headingStyle = {
    fontSize: "clamp(28px, 6vw, 48px)",
    fontFamily: "'Bebas Neue', sans-serif",
    textAlign: "center",
    marginBottom: 16,
    color: c.text,
    letterSpacing: 2,
  };

  const subStyle = {
    textAlign: "center",
    color: c.sub,
    fontSize: 16,
    marginBottom: 56,
    maxWidth: 500,
    margin: "0 auto 56px",
  };

  return (
    <div style={{ background: c.bg, color: c.text, minHeight: "100vh" }}>
      {/* Minimal CSS animations for hero only */}
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(3deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(79,195,247,0.1); }
          50% { box-shadow: 0 0 40px rgba(79,195,247,0.25); }
        }
      `}</style>

      {/* ══════ HEADER ══════ */}
      <header style={{
        padding: "14px 24px",
        background: "rgba(6,10,20,0.92)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        backdropFilter: "blur(16px)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "linear-gradient(135deg, #4fc3f7, #7c4dff)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M2 7l10-5 10 5-10 5L2 7z" fill="rgba(255,255,255,0.9)" />
              <path d="M2 7v10l10 5V12L2 7z" fill="rgba(255,255,255,0.6)" />
              <path d="M22 7v10l-10 5V12l10-5z" fill="rgba(255,255,255,0.75)" />
            </svg>
          </div>
          <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: c.text, letterSpacing: 1.5 }}>
            SHIP<span style={{ color: "#4fc3f7" }}>FAST</span>
          </span>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => navigate("/login")} style={{
            padding: "9px 20px", background: "transparent", border: "1px solid rgba(79,195,247,0.5)",
            color: "#4fc3f7", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 500,
            fontFamily: "'DM Sans', sans-serif",
          }}>Login</button>
          <button onClick={() => navigate("/login")} style={{
            padding: "9px 24px", background: "linear-gradient(135deg, #4fc3f7, #7c4dff)", border: "none",
            color: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
          }}>Get Started</button>
        </div>
      </header>

      {/* ══════ HERO ══════ */}
      <section style={{
        padding: "100px 24px 80px",
        background: "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(79,195,247,0.07) 0%, transparent 60%)",
        position: "relative", overflow: "hidden", textAlign: "center",
      }}>
        {/* Decorative floating elements — hero only */}
        <svg width="65" height="65" viewBox="0 0 60 60" fill="none" style={{ position: "absolute", top: "8%", left: "5%", opacity: 0.12, animation: "heroFloat 6s ease-in-out infinite", pointerEvents: "none" }}>
          <rect x="8" y="16" width="44" height="36" rx="3" stroke="#4fc3f7" strokeWidth="2" fill="rgba(79,195,247,0.05)" />
          <line x1="30" y1="16" x2="30" y2="52" stroke="#4fc3f7" strokeWidth="1.5" />
          <path d="M20 16L30 6L40 16" stroke="#4fc3f7" strokeWidth="2" fill="none" />
        </svg>
        <svg width="48" height="48" viewBox="0 0 44 44" fill="none" style={{ position: "absolute", top: "15%", right: "8%", opacity: 0.10, animation: "heroFloat 5s ease-in-out infinite", animationDelay: "1s", pointerEvents: "none" }}>
          <circle cx="22" cy="22" r="20" stroke="#10b981" strokeWidth="1.8" fill="rgba(16,185,129,0.04)" />
          <text x="22" y="28" textAnchor="middle" fill="#10b981" fontSize="20" fontWeight="700" fontFamily="sans-serif">$</text>
        </svg>

        <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "8px 20px", borderRadius: 40,
            background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)",
            color: "#10b981", fontSize: 13, fontWeight: 600, marginBottom: 28,
          }}>
            <span style={{ fontSize: 16 }}>✓</span> 100% Legit UPS Labels — Real Tracking, Full Insurance
          </div>

          <h1 style={{
            fontSize: "clamp(38px, 7.5vw, 76px)", fontFamily: "'Bebas Neue', sans-serif",
            fontWeight: "bold", letterSpacing: 3, lineHeight: 1.05, marginBottom: 24,
            background: "linear-gradient(135deg, #ffffff 0%, #4fc3f7 60%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>
            Finally — Legit UPS Labels<br/>Cheaper Than Amazon & Pirate Ship
          </h1>

          <p style={{
            fontSize: "clamp(16px, 2.2vw, 21px)", color: c.sub,
            maxWidth: 650, margin: "0 auto 20px", lineHeight: 1.7,
          }}>
            Same boxes. Same UPS service. Same tracking. Just way less money. No contracts, no minimums — fund your account and start shipping.
          </p>

          {/* Price callout */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 16, flexWrap: "wrap", justifyContent: "center",
            padding: "14px 28px", borderRadius: 12,
            background: "rgba(79,195,247,0.06)", border: "1px solid rgba(79,195,247,0.15)",
            marginBottom: 36, animation: "pulseGlow 3s ease-in-out infinite",
          }}>
            <span style={{ color: c.sub, fontSize: 15 }}>30 lb box on Pirate Ship:</span>
            <span style={{ color: "#ef4444", fontSize: 22, fontWeight: 800, textDecoration: "line-through", opacity: 0.7 }}>$58</span>
            <span style={{ color: "#4fc3f7", fontSize: 13, fontWeight: 600 }}>→</span>
            <span style={{ color: "#10b981", fontSize: 22, fontWeight: 800 }}>$37 here</span>
            <span style={{
              background: "rgba(16,185,129,0.12)", color: "#10b981",
              padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            }}>SAVE $21</span>
          </div>

          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/login")} style={{
              padding: "16px 44px", background: "linear-gradient(135deg, #4fc3f7, #7c4dff)",
              color: "#fff", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 700,
              fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
              boxShadow: "0 8px 24px rgba(79,195,247,0.2)",
            }}>Start Shipping Now</button>
            <button onClick={() => { const el = document.getElementById("pricing"); if (el) el.scrollIntoView({ behavior: "smooth" }); }} style={{
              padding: "16px 40px", background: "rgba(255,255,255,0.04)", color: "#4fc3f7",
              border: "1px solid rgba(79,195,247,0.3)", borderRadius: 10, fontSize: 16, fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
            }}>Compare Prices</button>
          </div>
        </div>
      </section>

      {/* ══════ SOCIAL PROOF BAR ══════ */}
      <section style={{
        padding: "20px 24px", background: "rgba(79,195,247,0.03)",
        borderTop: "1px solid rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}>
        <div style={{
          display: "flex", justifyContent: "center", alignItems: "center",
          gap: "clamp(20px, 5vw, 60px)", flexWrap: "wrap", maxWidth: 900, margin: "0 auto",
        }}>
          {[
            { num: "100%", label: "Legit UPS Labels" },
            { num: "30-50%", label: "Cheaper Than Competitors" },
            { num: "1 min", label: "Label Turnaround" },
            { num: "$0", label: "Monthly Fees" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "clamp(20px, 3vw, 28px)", fontWeight: 800, color: "#4fc3f7", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1 }}>{s.num}</div>
              <div style={{ fontSize: 12, color: c.sub, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════ HOW IT WORKS ══════ */}
      <section style={sectionStyle()}>
        <h2 style={headingStyle}>How It Works</h2>
        <p style={subStyle}>Three simple steps to cheaper shipping</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 32, maxWidth: 1000, margin: "0 auto" }}>
          {[
            { step: "01", title: "Create an Order", desc: "Enter your shipment details — sender, recipient, weight, and service. Takes under 2 minutes.", emoji: "📋" },
            { step: "02", title: "Get Your Price", desc: "We quote you a price way below retail. Fund your balance via Zelle and confirm the order.", emoji: "💰" },
            { step: "03", title: "Ship & Track", desc: "Get your UPS label in about 1 minute. Drop off the package and track delivery in real-time.", emoji: "🚚" },
          ].map((item, idx) => (
            <div key={idx} style={{
              background: c.card, border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16, padding: "36px 28px", textAlign: "center",
            }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>{item.emoji}</div>
              <div style={{ color: "#4fc3f7", fontSize: 13, fontWeight: 700, letterSpacing: 2, marginBottom: 8 }}>STEP {item.step}</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 10, color: c.text }}>{item.title}</h3>
              <p style={{ color: c.sub, fontSize: 14, lineHeight: 1.6 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════ PRICING COMPARISON ══════ */}
      <section id="pricing" style={sectionStyle({
        background: "radial-gradient(ellipse 70% 40% at 50% 50%, rgba(79,195,247,0.04) 0%, transparent 70%)",
      })}>
        <h2 style={headingStyle}>See The Difference</h2>
        <p style={subStyle}>Real prices. Same UPS service. You do the math.</p>

        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          {/* Table header */}
          <div style={{
            display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 0.8fr",
            padding: "14px 20px", borderRadius: "12px 12px 0 0",
            background: "rgba(79,195,247,0.08)", borderBottom: "1px solid rgba(79,195,247,0.15)",
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: c.sub, textTransform: "uppercase", letterSpacing: 1 }}>Shipment</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: 1, textAlign: "center" }}>Them</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#10b981", textTransform: "uppercase", letterSpacing: 1, textAlign: "center" }}>Us</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#4fc3f7", textTransform: "uppercase", letterSpacing: 1, textAlign: "center" }}>You Save</span>
          </div>

          {/* Price rows */}
          {pricingExamples.map((row, idx) => (
            <div key={idx} style={{
              display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 0.8fr", alignItems: "center",
              padding: "18px 20px",
              background: idx % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
            }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: c.text }}>{row.box}</div>
                <div style={{ fontSize: 12, color: c.muted }}>{row.competitor}</div>
              </div>
              <div style={{ textAlign: "center", color: "#ef4444", fontSize: 18, fontWeight: 700, textDecoration: "line-through", opacity: 0.6 }}>{row.competitorPrice}</div>
              <div style={{ textAlign: "center", color: "#10b981", fontSize: 20, fontWeight: 800 }}>{row.ourPrice}</div>
              <div style={{ textAlign: "center" }}>
                <span style={{
                  background: "rgba(16,185,129,0.1)", color: "#10b981",
                  padding: "4px 10px", borderRadius: 20, fontSize: 13, fontWeight: 700,
                }}>{row.save}</span>
              </div>
            </div>
          ))}

          <div style={{ padding: "12px 20px", textAlign: "center" }}>
            <span style={{ fontSize: 12, color: c.muted }}>* Prices shown are estimates for residential UPS Ground shipments. Actual prices may vary by zone.</span>
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: 48 }}>
          <p style={{ color: c.sub, fontSize: 16, marginBottom: 8 }}>No monthly fees. No contracts. No BS.</p>
          <p style={{ color: c.muted, fontSize: 14, marginBottom: 28 }}>Fund your account via Zelle and start saving on every shipment.</p>
          <button onClick={() => navigate("/login")} style={{
            padding: "18px 52px", background: "linear-gradient(135deg, #4fc3f7, #7c4dff)",
            color: "#fff", border: "none", borderRadius: 10, fontSize: 17, fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
            boxShadow: "0 8px 24px rgba(79,195,247,0.25)",
          }}>Start Saving Today</button>
        </div>
      </section>

      {/* ══════ SERVICES ══════ */}
      <section style={sectionStyle()}>
        <h2 style={headingStyle}>All UPS Services Available</h2>
        <p style={subStyle}>From Ground to Next Day Air — we've got you covered</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, maxWidth: 1000, margin: "0 auto" }}>
          {SERVICES.map((service) => (
            <div key={service.id} style={{
              background: c.card, border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 14, padding: "28px 22px", textAlign: "center",
            }}>
              <div style={{ fontSize: 38, marginBottom: 12 }}>{service.icon}</div>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6, color: c.text }}>{service.name}</h3>
              <p style={{ color: "#4fc3f7", fontSize: 14, fontWeight: 600 }}>{service.eta}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════ WHY US / FEATURES ══════ */}
      <section style={sectionStyle({ background: "rgba(17,24,39,0.3)" })}>
        <h2 style={headingStyle}>Why ShipFast?</h2>
        <p style={subStyle}>We keep it simple and we keep it real</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, maxWidth: 1000, margin: "0 auto" }}>
          {[
            { title: "100% Legit Labels", desc: "Real UPS labels with real tracking numbers. Not gray market, not resold leftovers. Fully legitimate with insurance.", emoji: "✅" },
            { title: "Way Cheaper", desc: "30-50% less than Amazon, Pirate Ship, and retail UPS. The same exact service for less money.", emoji: "💲" },
            { title: "1 Minute Labels", desc: "Get your label in about 1 minute after confirming. No waiting around for hours or days.", emoji: "⚡" },
            { title: "Real Support", desc: "In-app chat with a real person. Not a chatbot maze — actual help when you need it.", emoji: "💬" },
            { title: "Secure Balance System", desc: "Fund via Zelle, see your balance in real-time, auto-deduct on each order. Simple and transparent.", emoji: "🔒" },
            { title: "Works Everywhere", desc: "Use it on your phone, tablet, or computer. Install it as an app — no app store needed.", emoji: "📱" },
          ].map((f, idx) => (
            <div key={idx} style={{
              background: c.card, border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 14, padding: "28px 24px",
            }}>
              <div style={{ fontSize: 28, marginBottom: 14 }}>{f.emoji}</div>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: c.text }}>{f.title}</h3>
              <p style={{ color: c.sub, fontSize: 14, lineHeight: 1.6 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════ FAQ SECTION ══════ */}
      <section style={sectionStyle()}>
        <h2 style={headingStyle}>Frequently Asked Questions</h2>
        <p style={subStyle}>Everything you need to know</p>

        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          {faqItems.map((item, idx) => (
            <div key={idx} style={{
              marginBottom: 12,
              border: `1px solid ${openFaq === idx ? "rgba(79,195,247,0.3)" : "rgba(255,255,255,0.06)"}`,
              borderRadius: 12,
              background: openFaq === idx ? "rgba(79,195,247,0.04)" : c.card,
              overflow: "hidden",
            }}>
              <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} style={{
                width: "100%", padding: "20px 24px",
                background: "transparent", border: "none", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                color: c.text, fontSize: 16, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif", textAlign: "left",
              }}>
                <span>{item.q}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{
                  transform: openFaq === idx ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease", flexShrink: 0, marginLeft: 16,
                }}>
                  <path d="M5 8l5 5 5-5" stroke="#4fc3f7" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <div style={{
                maxHeight: openFaq === idx ? 300 : 0,
                overflow: "hidden",
                transition: "max-height 0.4s ease",
              }}>
                <div style={{
                  padding: "0 24px 20px",
                  color: c.sub, fontSize: 14, lineHeight: 1.7,
                }}>
                  {item.a}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════ FINAL CTA ══════ */}
      <section style={sectionStyle({
        textAlign: "center",
        background: "radial-gradient(ellipse 80% 50% at 50% 50%, rgba(79,195,247,0.06) 0%, transparent 60%)",
      })}>
        <h2 style={{
          fontSize: "clamp(30px, 6vw, 52px)", fontFamily: "'Bebas Neue', sans-serif",
          marginBottom: 16, color: c.text, letterSpacing: 2,
        }}>
          Stop Overpaying for Shipping
        </h2>
        <p style={{ fontSize: 18, color: c.sub, marginBottom: 36, maxWidth: 550, margin: "0 auto 36px", lineHeight: 1.6 }}>
          Join ShipFast and keep more money in your pocket on every package you send.
        </p>
        <button onClick={() => navigate("/login")} style={{
          padding: "18px 56px", background: "linear-gradient(135deg, #4fc3f7, #7c4dff)",
          color: "#fff", border: "none", borderRadius: 10, fontSize: 18, fontWeight: 700,
          fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
          boxShadow: "0 8px 32px rgba(79,195,247,0.3)",
        }}>Create Your Free Account</button>
        <p style={{ color: c.muted, fontSize: 13, marginTop: 14 }}>No credit card required. Fund via Zelle when you're ready.</p>
      </section>

      {/* ══════ FOOTER ══════ */}
      <footer style={{
        padding: "36px 24px", borderTop: "1px solid rgba(255,255,255,0.04)",
        background: "rgba(6,10,20,0.6)", textAlign: "center",
      }}>
        <div style={{ fontSize: 18, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1.5, color: "#4fc3f7", marginBottom: 6 }}>
          SHIPFAST
        </div>
        <p style={{ color: c.sub, fontSize: 13, marginBottom: 12 }}>
          Legit UPS labels at unbeatable prices
        </p>
        <p style={{ color: c.muted, fontSize: 11 }}>
          &copy; 2024-2026 ShipFast. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;
